/**
 * Admin Listing Detail Page
 *
 * The listings table has always linked each row here, but the page was never
 * built, so every row 404'd — there was no way to actually review a listing
 * before approving it. Shows the photos, location, pricing and host, and
 * hosts the approve / reject actions.
 */

import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../layout';
import { useAdminListing } from './hooks';
import { useListingActions } from './hooks';
import toast from 'react-hot-toast';

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  draft: 'bg-gray-100 text-gray-700',
  paused: 'bg-blue-100 text-blue-700',
  deleted: 'bg-red-100 text-red-700',
};

export const ListingDetailPage: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const navigate = useNavigate();
  const { listing, isLoading } = useAdminListing(listingId || '');
  const { approveListing, rejectListing } = useListingActions();

  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  // useAdminListing is a one-shot read, so reflect the change locally.
  const [statusNow, setStatusNow] = useState<string | null>(null);

  const handleApprove = async () => {
    if (!listingId) return;
    setBusy(true);
    const ok = await approveListing(listingId);
    setBusy(false);
    if (ok) {
      setStatusNow('active');
      toast.success('Listing approved and published.');
    } else {
      toast.error("Couldn't approve this listing. Please try again.");
    }
  };

  const handleReject = async () => {
    if (!listingId || !reason.trim()) return;
    setBusy(true);
    const ok = await rejectListing(listingId, reason.trim());
    setBusy(false);
    if (ok) {
      setStatusNow('draft');
      setRejecting(false);
      toast.success('Listing rejected and sent back to the host.');
    } else {
      toast.error("Couldn't reject this listing. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="p-6 animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-64" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </AdminLayout>
    );
  }

  if (!listing) {
    return (
      <AdminLayout>
        <div className="p-6 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Listing not found</h1>
          <Link to="/admin/listings" className="text-primary-600 hover:underline">
            Back to listings
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const status = statusNow || listing.status;

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl">
        <Link
          to="/admin/listings"
          className="text-sm text-gray-500 hover:text-gray-900 mb-4 inline-block"
        >
          ← Back to listings
        </Link>

        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{listing.title}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {listing.propertyType} · {listing.location.city}
              {listing.location.region ? `, ${listing.location.region}` : ''}
            </p>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
              statusColors[status] || ''
            }`}
          >
            {status}
          </span>
        </div>

        {/* Photos — the main thing to actually review */}
        <section className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="font-semibold text-lg mb-4">
            Photos{' '}
            <span className="text-sm font-normal text-gray-500">
              ({listing.photos.length})
            </span>
          </h2>
          {listing.photos.length === 0 ? (
            <p className="text-sm text-gray-500">
              No photos. A listing with no photos should not be approved.
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {listing.photos.map((photo, i) => (
                <a
                  key={i}
                  href={photo.url}
                  target="_blank"
                  rel="noreferrer"
                  className="block aspect-[4/3] rounded-lg overflow-hidden bg-gray-100"
                >
                  <img
                    src={photo.url}
                    alt={photo.caption || `Photo ${i + 1}`}
                    className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                  />
                </a>
              ))}
            </div>
          )}
        </section>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-6">
            <section className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-lg mb-4">Details</h2>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-gray-500">Nightly rate</dt>
                  <dd className="font-medium">
                    ${listing.pricing.nightlyRate} {listing.pricing.currency}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Property type</dt>
                  <dd className="font-medium">{listing.propertyType}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Location</dt>
                  <dd className="font-medium">
                    {listing.location.city}, {listing.location.country}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Bookings</dt>
                  <dd className="font-medium">{listing.bookingsCount}</dd>
                </div>
              </dl>
            </section>

            <section className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-lg mb-4">Host</h2>
              <p className="text-sm font-medium">{listing.hostName}</p>
              <p className="text-sm text-gray-500">{listing.hostEmail}</p>
              <p className="text-xs text-gray-400 mt-1 break-all">
                hostId: {listing.hostId}
              </p>
            </section>
          </div>

          {/* Review actions */}
          <div className="md:col-span-1">
            <section className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
              <h2 className="font-semibold text-lg mb-1">Review</h2>
              <p className="text-sm text-gray-500 mb-4">
                Approving publishes the listing so guests can book it.
              </p>

              {status === 'active' ? (
                <div className="rounded-lg bg-green-50 text-green-800 text-sm p-3 mb-3">
                  This listing is published.
                </div>
              ) : null}

              <div className="space-y-3">
                <button
                  onClick={handleApprove}
                  disabled={busy || status === 'active'}
                  className="w-full py-2.5 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {busy ? 'Working…' : 'Approve & publish'}
                </button>

                {rejecting ? (
                  <div className="space-y-2">
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={3}
                      placeholder="Why is this being rejected? The host sees this."
                      className="w-full rounded-lg border-gray-300 text-sm focus:ring-primary-500 focus:border-primary-500"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleReject}
                        disabled={busy || !reason.trim()}
                        className="flex-1 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50"
                      >
                        Confirm reject
                      </button>
                      <button
                        onClick={() => setRejecting(false)}
                        className="px-3 py-2 border border-gray-300 text-sm rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setRejecting(true)}
                    disabled={busy}
                    className="w-full py-2.5 border border-red-300 text-red-700 font-semibold rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    Reject
                  </button>
                )}

                <button
                  onClick={() => navigate(`/listing/${listing.id}`)}
                  className="w-full py-2.5 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  View public page
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ListingDetailPage;
